import { useEffect, useState } from "react";
import { Star, Pencil } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StarRating, FeedbackForm } from "@/components/FeedbackForm";

// Shown on the Job Detail record. Visible to Super Admin, Studio Owner, Studio Manager, CRM.
export const JobFeedbackCard = ({ jobId }) => {
  const { user } = useAuth();
  const canRead = can(user, "feedback:read");
  const canCollect = can(user, "feedback:collect");
  const [fb, setFb] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = () =>
    api.get(`/jobs/${jobId}/feedback`).then(({ data }) => {
      setFb(data);
      setLoaded(true);
    }).catch(() => setLoaded(true));

  useEffect(() => {
    if (canRead) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  if (!canRead || !loaded) return null;

  return (
    <Card className="mt-4 border-brand-line shadow-none" data-testid="job-feedback-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Star className="h-4 w-4 text-amber-400" strokeWidth={1.8} /> Customer feedback
        </CardTitle>
        {fb && canCollect && !editing && (
          <Button size="sm" variant="outline" data-testid="edit-feedback-btn" onClick={() => setEditing(true)}>
            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <FeedbackForm
            jobId={jobId}
            initial={fb}
            onSaved={(d) => {
              setFb(d);
              setEditing(false);
            }}
          />
        ) : fb ? (
          <div className="space-y-3" data-testid="feedback-summary">
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat label="Overall" value={fb.overall} />
              <Stat label="Quality" value={fb.quality} />
              <Stat label="Staff behaviour" value={fb.staff_behaviour} />
            </div>
            <p className="text-sm text-brand-ink">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Would recommend:{" "}
              </span>
              <span className={fb.recommend ? "text-emerald-600" : "text-crimson-600"}>
                {fb.recommend ? "Yes" : "No"}
              </span>
            </p>
            {fb.comments && <p className="text-sm text-slate-600">"{fb.comments}"</p>}
            <p className="text-xs text-slate-400">
              Collected by {fb.collected_by_name} · {fmtDateTime(fb.updated_at || fb.collected_at)}
            </p>
          </div>
        ) : canCollect ? (
          <FeedbackForm jobId={jobId} onSaved={(d) => setFb(d)} />
        ) : (
          <p className="text-sm text-slate-400" data-testid="feedback-none">
            Feedback not collected yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <StarRating value={value} readOnly />
  </div>
);
